import { useState } from "react"
import { auth } from "../components/Authenticator/firebase"
import { useNavigate } from "react-router-dom"
import { API_URL } from "../config/api"
import { 
  Database, 
  Play, 
  Download, 
  Home,
  ChevronDown,
  ChevronUp,
  FileText,
  Copy,
  Check,
  Loader2,
  AlertCircle,
  Eye,
  EyeOff
} from "lucide-react"

// SQL fija para generar datos vectoriales del huerto
const FIXED_SQL = `SELECT 
    a.id AS Codigo_Referencia,
    fg.FM_DENO AS Denominacion_Grupo,
    ff.FM_DENO AS Denominacion_Familia,
    a.AR_PVPIVA AS Precio_Fisico,
    a.AR_DESWEB AS Denominacion_Web,
    a.AR_PVPWEBI AS Precio_Web,
    CASE 
        WHEN a.AR_WSN = 'S' THEN 'Si' 
        ELSE 'No' 
    END AS Articulo_subido_en_la_web,
    CASE 
        WHEN a.AR_ESTWEB = '1' THEN 'Si' 
        ELSE 'No' 
    END AS Se_encuentra_disponible_en_la_web,
    a.AR_PLBN AS Plantas_por_bandeja,
    a.AR_PVBN AS Precio_de_venta_bandeja,
    a.AR_DSBN AS Descripcion_Bandeja,
    FLOOR(COALESCE(stock_total.stock_fisico, 0) * COALESCE(a.AR_STCWEB, 0) / 100) AS Stock_Web,
    COALESCE(stock_total.stock_fisico, 0) AS Stock_Fisico,
    COALESCE(GROUP_CONCAT(obs.C0 ORDER BY obs.id2 SEPARATOR ' '), '') AS Descripcion_de_cada_articulo
FROM 
    articulos a
LEFT JOIN 
    familias fg ON a.AR_GRP = fg.id
LEFT JOIN 
    familias ff ON a.AR_FAM = ff.id
LEFT JOIN (
    SELECT 
        id,
        SUM(C1) AS stock_fisico
    FROM 
        articulos_ar_ubic
    GROUP BY 
        id
) stock_total ON a.id = stock_total.id
LEFT JOIN 
    articulos_ar_obsweb obs ON a.id = obs.id
GROUP BY 
    a.id,
    fg.FM_DENO,
    ff.FM_DENO,
    a.AR_PVPIVA,
    a.AR_DESWEB,
    a.AR_PVPWEBI,
    a.AR_WSN,
    a.AR_ESTWEB,
    a.AR_PLBN,
    a.AR_PVBN,
    a.AR_DSBN,
    a.AR_STCWEB,
    stock_total.stock_fisico
ORDER BY 
    a.id;`

export default function VectorGenerator() {
  const navigate = useNavigate()
  
  const [showSQL, setShowSQL] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [results, setResults] = useState(null)
  const [txtOutput, setTxtOutput] = useState("")
  const [copied, setCopied] = useState(false)
  const [showPreview, setShowPreview] = useState(true)
  const [stats, setStats] = useState({ total: 0, executionTime: 0 })

  // Formatear un registro como texto para base vectorial
  const formatRecordForVector = (record) => {
    const lines = []
    
    lines.push(`Codigo_Referencia: ${record.Codigo_Referencia || 'N/A'}`)
    lines.push(`Denominacion_Grupo: ${record.Denominacion_Grupo || 'N/A'}`)
    lines.push(`Denominacion_Familia: ${record.Denominacion_Familia || 'N/A'}`)
    lines.push(`Precio_Fisico: ${record.Precio_Fisico !== null ? record.Precio_Fisico : 'N/A'}`)
    lines.push(`Denominacion_Web: ${record.Denominacion_Web || 'N/A'}`)
    lines.push(`Precio_Web: ${record.Precio_Web !== null ? record.Precio_Web : 'N/A'}`)
    lines.push(`Articulo_subido_en_la_web: ${record.Articulo_subido_en_la_web || 'N/A'}`)
    lines.push(`Se_encuentra_disponible_en_la_web: ${record.Se_encuentra_disponible_en_la_web || 'N/A'}`)
    lines.push(`Plantas_por_bandeja: ${record.Plantas_por_bandeja !== null ? record.Plantas_por_bandeja : 'N/A'}`)
    lines.push(`Precio_de_venta_bandeja: ${record.Precio_de_venta_bandeja !== null ? record.Precio_de_venta_bandeja : 'N/A'}`)
    lines.push(`Descripcion_Bandeja: ${record.Descripcion_Bandeja || 'N/A'}`)
    lines.push(`Stock_Web: ${record.Stock_Web !== null ? record.Stock_Web : 'N/A'}`)
    lines.push(`Stock_Fisico: ${record.Stock_Fisico !== null ? record.Stock_Fisico : 'N/A'}`)
    lines.push(`Descripcion_de_cada_articulo: ${record.Descripcion_de_cada_articulo || 'N/A'}`)
    
    return lines.join('\n')
  }

  // Generar el TXT completo
  const generateTXT = (data) => {
    const separator = "================="
    const formattedRecords = data.map(record => formatRecordForVector(record))
    return formattedRecords.join(`\n${separator}\n`)
  }

  // Ejecutar la consulta SQL
  const executeQuery = async () => {
    try {
      setLoading(true)
      setError(null)
      setResults(null)
      setTxtOutput("")

      const token = await auth.currentUser?.getIdToken()
      if (!token) {
        navigate("/login")
        return
      }

      const startTime = Date.now()
      
      const response = await fetch(`${API_URL}/api/chat/vector/generate-huerto`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Error al ejecutar la consulta')
      }

      const executionTime = Date.now() - startTime
      
      setResults(data.data)
      setStats({ total: data.total, executionTime })
      
      // Generar el TXT formateado
      const txt = generateTXT(data.data)
      setTxtOutput(txt)

    } catch (err) {
      console.error('Error:', err)
      setError(err.message || 'Error desconocido')
    } finally {
      setLoading(false)
    }
  }

  // Copiar al portapapeles
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(txtOutput)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Error copiando:', err)
    }
  }

  // Descargar como archivo TXT
  const downloadTXT = () => {
    const blob = new Blob([txtOutput], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `articulos_huerto_vectorial_${new Date().toISOString().split('T')[0]}.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      minHeight: "100vh",
      background: "#fbf9f4",
      fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif"
    }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .sql-code {
          font-family: 'Fira Code', 'Monaco', monospace;
          font-size: 12px;
          line-height: 1.5;
          white-space: pre-wrap;
          word-break: break-all;
        }
      `}</style>

      {/* Header */}
      <div style={{
        display: "flex",
        background: "#fff",
        borderBottom: "1px solid #e5e2dc",
        padding: "0 24px",
        alignItems: "center",
        height: "64px",
        flexShrink: 0
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{
            width: "40px",
            height: "40px",
            borderRadius: "10px",
            background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}>
            <Database size={20} color="#fff" />
          </div>
          <div>
            <h1 style={{ fontSize: "16px", fontWeight: "700", color: "#1f2937", margin: 0 }}>
              Generador Vectorial Huerto
            </h1>
            <p style={{ fontSize: "12px", color: "#6b7280", margin: 0 }}>
              Exportar artículos para base vectorial
            </p>
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <button
          onClick={() => navigate("/home")}
          style={{
            background: "none",
            border: "none",
            padding: "10px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            color: "#6b7280",
            borderRadius: "8px",
            transition: "background 0.2s"
          }}
          onMouseEnter={(e) => e.target.style.background = "#f3f4f6"}
          onMouseLeave={(e) => e.target.style.background = "none"}
        >
          <Home size={22} />
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, padding: "24px", maxWidth: "1200px", margin: "0 auto", width: "100%", boxSizing: "border-box" }}>
        
        {/* SQL Oculta (Colapsable) */}
        <div style={{
          background: "#fff",
          borderRadius: "12px",
          border: "1px solid #e5e2dc",
          marginBottom: "20px",
          overflow: "hidden"
        }}>
          <button
            onClick={() => setShowSQL(!showSQL)}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "16px 20px",
              background: "#f9fafb",
              border: "none",
              cursor: "pointer",
              borderBottom: showSQL ? "1px solid #e5e2dc" : "none"
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <FileText size={18} color="#6b7280" />
              <span style={{ fontSize: "14px", fontWeight: "600", color: "#374151" }}>
                Consulta SQL (Base de datos: huerto)
              </span>
            </div>
            {showSQL ? <ChevronUp size={18} color="#6b7280" /> : <ChevronDown size={18} color="#6b7280" />}
          </button>
          
          {showSQL && (
            <div style={{ padding: "16px 20px", background: "#1e293b", color: "#e2e8f0" }}>
              <pre className="sql-code" style={{ margin: 0 }}>
                {FIXED_SQL}
              </pre>
            </div>
          )}
        </div>

        {/* Botón Ejecutar */}
        <div style={{ marginBottom: "24px" }}>
          <button
            onClick={executeQuery}
            disabled={loading}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "14px 28px",
              background: loading ? "#9ca3af" : "linear-gradient(135deg, #059669 0%, #047857 100%)",
              color: "#fff",
              border: "none",
              borderRadius: "10px",
              fontSize: "15px",
              fontWeight: "600",
              cursor: loading ? "not-allowed" : "pointer",
              boxShadow: "0 4px 12px rgba(5, 150, 105, 0.3)",
              transition: "all 0.2s"
            }}
          >
            {loading ? (
              <>
                <Loader2 size={20} style={{ animation: "spin 1s linear infinite" }} />
                Ejecutando consulta...
              </>
            ) : (
              <>
                <Play size={20} />
                Ejecutar y Generar TXT
              </>
            )}
          </button>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            padding: "16px 20px",
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: "10px",
            marginBottom: "20px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            color: "#dc2626"
          }}>
            <AlertCircle size={20} />
            <span style={{ fontSize: "14px", fontWeight: "500" }}>{error}</span>
          </div>
        )}

        {/* Resultados */}
        {results && (
          <div style={{
            background: "#fff",
            borderRadius: "12px",
            border: "1px solid #e5e2dc",
            overflow: "hidden"
          }}>
            {/* Header de resultados */}
            <div style={{
              padding: "16px 20px",
              borderBottom: "1px solid #e5e2dc",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: "#f9fafb"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                <div style={{
                  background: "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)",
                  padding: "8px 14px",
                  borderRadius: "8px",
                  border: "1px solid #86efac"
                }}>
                  <span style={{ fontSize: "13px", color: "#166534", fontWeight: "600" }}>
                    {stats.total} artículos
                  </span>
                </div>
                <span style={{ fontSize: "12px", color: "#6b7280" }}>
                  Ejecutado en {stats.executionTime}ms
                </span>
              </div>
              
              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "8px 14px",
                    background: "#f3f4f6",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontWeight: "500",
                    color: "#374151",
                    cursor: "pointer"
                  }}
                >
                  {showPreview ? <EyeOff size={16} /> : <Eye size={16} />}
                  {showPreview ? "Ocultar" : "Mostrar"}
                </button>
                
                <button
                  onClick={copyToClipboard}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "8px 14px",
                    background: copied ? "#dcfce7" : "#f3f4f6",
                    border: `1px solid ${copied ? "#86efac" : "#e5e7eb"}`,
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontWeight: "500",
                    color: copied ? "#166534" : "#374151",
                    cursor: "pointer"
                  }}
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                  {copied ? "Copiado!" : "Copiar"}
                </button>
                
                <button
                  onClick={downloadTXT}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "8px 14px",
                    background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#fff",
                    cursor: "pointer",
                    boxShadow: "0 2px 8px rgba(37, 99, 235, 0.3)"
                  }}
                >
                  <Download size={16} />
                  Descargar TXT
                </button>
              </div>
            </div>

            {/* Preview del TXT */}
            {showPreview && (
              <div style={{
                maxHeight: "500px",
                overflowY: "auto",
                padding: "20px",
                background: "#1e293b",
                color: "#e2e8f0",
                fontFamily: "'Fira Code', 'Monaco', monospace",
                fontSize: "12px",
                lineHeight: "1.6",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word"
              }}>
                {txtOutput}
              </div>
            )}
          </div>
        )}

        {/* Información de uso */}
        {!results && !loading && (
          <div style={{
            background: "#fff",
            borderRadius: "12px",
            border: "1px solid #e5e2dc",
            padding: "32px",
            textAlign: "center"
          }}>
            <div style={{
              width: "64px",
              height: "64px",
              borderRadius: "16px",
              background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px"
            }}>
              <FileText size={32} color="#059669" />
            </div>
            <h2 style={{ fontSize: "18px", fontWeight: "700", color: "#1f2937", marginBottom: "12px" }}>
              Generador de datos para Base Vectorial
            </h2>
            <p style={{ fontSize: "14px", color: "#6b7280", maxWidth: "500px", margin: "0 auto 20px", lineHeight: "1.6" }}>
              Esta herramienta ejecuta una consulta SQL sobre la base de datos <strong>huerto</strong> y genera 
              un archivo TXT formateado para ser subido a una base vectorial (como Pinecone).
            </p>
            <div style={{
              background: "#f9fafb",
              borderRadius: "10px",
              padding: "16px",
              display: "inline-block",
              textAlign: "left"
            }}>
              <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "8px", fontWeight: "600" }}>
                Formato de salida:
              </div>
              <pre style={{
                margin: 0,
                fontFamily: "'Fira Code', monospace",
                fontSize: "11px",
                color: "#374151",
                lineHeight: "1.5"
              }}>
{`Codigo_Referencia: 12345
Denominacion_Grupo: Plantas
Denominacion_Familia: Hortalizas
...
=================
Codigo_Referencia: 12346
...`}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
